import type { DataTexture, MeshPhysicalMaterial } from 'three';
import {
  findProformGlassPreset,
  GENERIC_PROFORM_GLASS,
  type ProformGlassMaterialDescriptor,
} from './proform-glass-presets';
import { sampleProformGlassPattern } from './proform-glass-pattern';
export type { ProformGlassMaterialDescriptor } from './proform-glass-presets';

type ThreeModule = typeof import('three');

export type ProformGlassSelection = {
  groupCode?: string;
  fillingCode?: string;
  orientation?: string;
  ornamentSide?: string;
  /** Validated HTML colour from the selected user_sklo_spec API option. */
  color?: string;
};

export type ProformGlassDimensions = {
  /** Actual finished glass-sheet dimensions, not native OBJ dimensions. */
  widthMm: number;
  heightMm: number;
};

export type ProformGlassMaterialOptions = {
  maxAnisotropy?: number;
};

export type ProformGlassMaterialInstance = {
  material: MeshPhysicalMaterial;
  descriptor: ProformGlassMaterialDescriptor;
  textures: DataTexture[];
  /** Kept with the instance so updates do not need another dynamic import. */
  THREE: ThreeModule;
  patternKey: string;
};

const normalizeCode = (value: string | undefined) => value?.trim().toUpperCase() ?? '';
const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

/** Unknown options remain explicitly generic; the selection photo is never a material map. */
export function resolveProformGlassMaterial(
  selection: ProformGlassSelection = {},
): ProformGlassMaterialDescriptor {
  const preset = findProformGlassPreset(selection.groupCode, selection.fillingCode);
  const descriptor = preset ?? GENERIC_PROFORM_GLASS;
  const color = /^#[\da-f]{6}$/i.test(selection.color ?? '') ? selection.color : undefined;
  const ornamentSide = normalizeCode(selection.ornamentSide);
  return {
    ...descriptor,
    ...(preset?.colorSpecification
      ? { color: color ?? descriptor.color, warning: color ? undefined : ('color-needed' as const) }
      : {}),
    ...(descriptor.pattern ? { pattern: { ...descriptor.pattern } } : {}),
    horizontal: Boolean(
      descriptor.pattern?.directional && normalizeCode(selection.orientation) === '20',
    ),
    ornamentSide: ornamentSide === '10' ? 'inner' : ornamentSide === '20' ? 'outer' : undefined,
  };
}

export function getProformGlassPatternRepeat(
  descriptor: ProformGlassMaterialDescriptor,
  dimensions: ProformGlassDimensions,
) {
  const pitch = descriptor.pattern?.pitchMm;
  if (!pitch || !Number.isFinite(pitch) || pitch <= 0) return { x: 1, y: 1 };
  const repeats = (length: number) => (Number.isFinite(length) && length > 0 ? length / pitch : 1);
  return { x: repeats(dimensions.widthMm), y: repeats(dimensions.heightMm) };
}

function patternKey(descriptor: ProformGlassMaterialDescriptor) {
  return descriptor.pattern ? JSON.stringify(descriptor.pattern) : '';
}

function createPatternTextures(THREE: ThreeModule, descriptor: ProformGlassMaterialDescriptor) {
  const pattern = descriptor.pattern;
  if (!pattern) return [];

  // One repeat is a single physical lattice cell. No reference/sample photograph
  // is projected onto the glass; roughness controls blur and bump supplies relief.
  const size = 128;
  const roughnessData = new Uint8Array(size * size * 4);
  const bumpData = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const { roughness, height } = sampleProformGlassPattern(
        pattern,
        (x + 0.5) / size,
        (y + 0.5) / size,
      );
      const roughnessByte = Math.round(clamp(roughness, 0, 1) * 255);
      const reliefByte = Math.round(height * 255);
      const offset = (y * size + x) * 4;
      roughnessData.set([roughnessByte, roughnessByte, roughnessByte, 255], offset);
      bumpData.set([reliefByte, reliefByte, reliefByte, 255], offset);
    }
  }

  return [roughnessData, bumpData].map((data, index) => {
    const texture = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
    texture.name = `proform-${descriptor.id}-${index === 0 ? 'roughness' : 'relief'}`;
    texture.colorSpace = THREE.NoColorSpace;
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.magFilter = THREE.LinearFilter;
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    texture.generateMipmaps = true;
    texture.needsUpdate = true;
    return texture;
  });
}

/**
 * Use on geometry expressed in actual millimetres under an unscaled parent.
 * Three's transmission thickness is mesh-local; a scaled OBJ parent must not
 * stretch the sheet. The renderer also needs opaque scene content behind it.
 */
export function createProformGlassMaterial(
  THREE: ThreeModule,
  descriptor: ProformGlassMaterialDescriptor,
  dimensions: ProformGlassDimensions,
  options: ProformGlassMaterialOptions = {},
): ProformGlassMaterialInstance {
  const instance: ProformGlassMaterialInstance = {
    THREE,
    descriptor,
    material: new THREE.MeshPhysicalMaterial(),
    patternKey: '',
    textures: [],
  };
  updateProformGlassMaterial(instance, descriptor, dimensions, options);
  return instance;
}

export function updateProformGlassMaterial(
  instance: ProformGlassMaterialInstance,
  descriptor: ProformGlassMaterialDescriptor,
  dimensions: ProformGlassDimensions,
  options: ProformGlassMaterialOptions = {},
) {
  const nextPatternKey = patternKey(descriptor);
  const { material, THREE } = instance;
  if (nextPatternKey !== instance.patternKey) {
    instance.textures.forEach(texture => texture.dispose());
    instance.textures = createPatternTextures(THREE, descriptor);
    instance.patternKey = nextPatternKey;
  }

  material.name = `proform-glass-${descriptor.id}`;
  material.color.set(descriptor.color);
  material.metalness = descriptor.family === 'mirror' ? 1 : 0;
  material.opacity = 1;
  material.transparent = false;
  material.depthWrite = descriptor.transmission === 0;
  // The pane geometry supplies its own outward-facing surfaces. Double-sided
  // transmissive materials enter Three's extra back-face transmission pass on
  // some WebGL/ANGLE capability paths; that pass can flash dark tiles during
  // rotation. FrontSide still renders both views of a closed pane via each
  // surface's outward face, without sampling the artificial second pass.
  material.side = THREE.FrontSide;
  material.ior = descriptor.ior;
  material.transmission = descriptor.transmission;
  material.roughness = descriptor.roughness;
  material.thickness = descriptor.thicknessMm;
  material.clearcoat = descriptor.family === 'lacquer' ? 1 : 0.08;
  material.clearcoatRoughness = 0.12;
  material.roughnessMap = instance.textures[0] ?? null;
  material.bumpMap = instance.textures[1] ?? null;
  // This sheet is an illustrative two-sided surface, not a measured glass
  // volume. Reverse its subtle relief when the ornament faces the cabinet.
  material.bumpScale =
    (descriptor.pattern?.reliefMm ?? 0) * (descriptor.ornamentSide === 'inner' ? -1 : 1);
  material.needsUpdate = true;

  const repeat = getProformGlassPatternRepeat(descriptor, dimensions);
  const anisotropy = options.maxAnisotropy;
  instance.textures.forEach(texture => {
    texture.repeat.set(repeat.x, repeat.y);
    // Rotate in physical millimetres, not normalized UV space (which stretches
    // horizontal stripes on a non-square door). Do not rebuild maps on resize.
    texture.matrixAutoUpdate = false;
    if (descriptor.horizontal) texture.matrix.set(0, repeat.y, 0, -repeat.x, 0, 0, 0, 0, 1);
    else texture.matrix.set(repeat.x, 0, 0, 0, repeat.y, 0, 0, 0, 1);
    if (anisotropy !== undefined && Number.isFinite(anisotropy)) {
      texture.anisotropy = clamp(anisotropy, 1, 8);
    }
    texture.needsUpdate = true;
  });
  instance.descriptor = descriptor;
}

export function disposeProformGlassMaterial(instance: ProformGlassMaterialInstance) {
  instance.textures.forEach(texture => texture.dispose());
  instance.textures = [];
  instance.material.roughnessMap = null;
  instance.material.bumpMap = null;
  instance.material.dispose();
}
